import { call, put, take, select, all } from 'redux-saga/effects'
/* eslint-disable camelcase */
import jwt_decode from 'jwt-decode'
/* eslint-enable camelcase */
import { callApi, addItemsToCart } from '../../api/api'
import { apiSaga, formSaga } from './sagas'
import { locationRoute, loginRoute, friendsRoute } from '../../routes/routes'
import { formatUrl } from '../../util/formatUtils'
import { push } from 'connected-react-router'
import {
  selectCurrentUser,
  selectUserLocationId,
  getLocationIdParam,
  selectConfirmUserCredentials,
  selectProfile,
  selectRef,
  selectCurrentDeliveryDropoff
} from './selectors'
import { JOIN_USER_SUCCESS, joinUser } from './join'
import { SUCCESS } from './locationSearch'
import {
  normalizeUserCreationError,
  normalizeErrorResponse,
  logException
} from '../../util/errorUtils'
import {
  storeTokens,
  getDecodedAccessToken,
  removeTokens
} from '../../util/webStorageUtils'
import { refreshTokenSaga } from './token'
import {
  loadProfileByUserId,
  loadProfileSuccess,
  loadProfileFailure
} from './profile'
import { getLocalCart, deleteLocalCart } from '../../util/webStorageUtils'
import LogRocket from 'logrocket'

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID
const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET

// ------------------------------------
// Action Types & Creators
// ------------------------------------

export const LOGIN = 'foodsby/user/LOGIN'
export const LOGIN_SUCCESS = 'foodsby/user/LOGIN_SUCCESS'
export const LOGIN_FAILURE = 'foodsby/user/LOGIN_FAILURE'

export const LOGOUT = 'foodsby/user/LOGOUT'
export const LOGOUT_SUCCESS = 'foodsby/user/LOGOUT_SUCCESS'
export const LOGOUT_FAILURE = 'foodsby/user/LOGOUT_FAILURE'

export const GET_CURRENT_USER = 'foodsby/user/GET_CURRENT_USER'
export const GET_CURRENT_USER_SUCCESS = 'foodsby/user/GET_CURRENT_USER_SUCCESS'
export const GET_CURRENT_USER_FAILURE = 'foodsby/user/GET_CURRENT_USER_FAILURE'

export const IS_ADMIN_SUCCESS = 'foodsby/user/IS_ADMIN_SUCCESS'
export const IS_ADMIN_FAILURE = 'foodsby/user/IS_ADMIN_FAILURE'

export const CONFIRM_USER = 'foodsby/user/CONFIRM_USER'
export const CONFIRM_USER_SUCCESS = 'foodsby/user/CONFIRM_USER_SUCCESS'
export const CONFIRM_USER_FAILURE = 'foodsby/user/CONFIRM_USER_FAILURE'

export const CONFIRM_PASSWORD = 'foodsby/user/CONFIRM_PASSWORD'
export const CONFIRM_PASSWORD_SUCCESS = 'foodsby/user/CONFIRM_PASSWORD_SUCCESS'
export const CONFIRM_PASSWORD_FAILURE = 'foodsby/user/CONFIRM_PASSWORD_FAILURE'

export const REGISTER = 'foodsby/ticket/register/REGISTER'
export const REGISTER_SUCCESS = 'foodsby/ticket/register/REGISTER_SUCCESS'
export const REGISTER_FAILURE = 'foodsby/ticket/register/REGISTER_FAILURE'
export const REGISTER_LIGHT = 'foodsby/ticket/register/REGISTER_LIGHT'
export const REGISTER_LIGHT_SUCCESS =
  'foodsby/ticket/register/REGISTER_LIGHT_SUCCESS'
export const REGISTER_LIGHT_FAILURE =
  'foodsby/ticket/register/REGISTER_LIGHT_FAILURE'

export const UPDATE_AND_SAVE_USER_LOCATION =
  'foodsby/user/UPDATE_AND_SAVE_USER_LOCATION'
export const UPDATE_AND_SAVE_USER_LOCATION_SUCCESS =
  'foodsby/user/UPDATE_AND_SAVE_USER_LOCATION_SUCCESS'
export const UPDATE_AND_SAVE_USER_LOCATION_FAILURE =
  'foodsby/user/UPDATE_AND_SAVE_USER_LOCATION_FAILURE'

export const UPDATE_USER_LOCATION_TEMPORARILY =
  'foodsby/user/UPDATE_USER_LOCATION_TEMPORARILY'
export const REVERT_TEMPORARY_LOCATION =
  'foodsby/user/REVERT_TEMPORARY_LOCATION'

export const loginStart = (credentials, ref) => {
  return {
    type: LOGIN,
    payload: { credentials, ref }
  }
}

export const loginSuccess = response => {
  return {
    type: LOGIN_SUCCESS,
    payload: response
  }
}

export const loginFailure = error => {
  const message = normalizeErrorResponse(error.response, error.message)
  return {
    type: LOGIN_FAILURE,
    error: { message }
  }
}

export const logoutStart = ref => {
  return {
    type: LOGOUT,
    payload: { ref }
  }
}

export const logoutSuccess = () => {
  return {
    type: LOGOUT_SUCCESS
  }
}

export const logoutFailure = error => {
  return {
    type: LOGOUT_FAILURE,
    error
  }
}

export const currentUserStart = (
  overrideRedirect = false,
  overrideStateCache = false
) => {
  return {
    type: GET_CURRENT_USER,
    payload: { overrideRedirect, overrideStateCache }
  }
}

export const registerStart = ({ username, password }) => {
  return {
    type: REGISTER,
    payload: {
      username,
      password
    }
  }
}

export const registerSuccess = currentUser => {
  return {
    type: REGISTER_SUCCESS,
    payload: { currentUser }
  }
}

export const registerFailure = error => {
  return {
    type: REGISTER_FAILURE,
    error
  }
}

export const registerLightStart = userData => {
  return {
    type: REGISTER_LIGHT,
    payload: { userData }
  }
}

export const registerLightSuccess = ({ username }) => {
  return {
    type: REGISTER_LIGHT_SUCCESS,
    payload: { username }
  }
}

export const registerLightFailure = error => {
  return {
    type: REGISTER_LIGHT_FAILURE,
    error
  }
}

export const confirmRegisterStart = (formData, path) => {
  return {
    type: CONFIRM_USER,
    payload: { formData, path }
  }
}

export const confirmRegisterSuccess = currentUser => {
  return {
    type: CONFIRM_USER_SUCCESS,
    payload: { currentUser }
  }
}

export const confirmPasswordStart = formData => {
  return {
    type: CONFIRM_PASSWORD,
    payload: { formData }
  }
}

export const confirmPasswordSuccess = currentUser => {
  return {
    type: CONFIRM_PASSWORD_SUCCESS,
    payload: { currentUser }
  }
}

export const confirmPasswordFailure = error => {
  return {
    type: CONFIRM_PASSWORD_FAILURE,
    error
  }
}

export const isAdminSuccess = data => {
  return {
    type: IS_ADMIN_SUCCESS,
    payload: {
      isAdmin: data.isAdmin
    }
  }
}

export const isAdminFailure = error => {
  return {
    type: IS_ADMIN_FAILURE,
    payload: error
  }
}

export const confirmRegisterFailure = error => {
  return {
    type: CONFIRM_USER_FAILURE,
    error
  }
}

export const currentUserSuccess = currentUser => {
  return {
    type: GET_CURRENT_USER_SUCCESS,
    payload: { currentUser }
  }
}

export const currentUserFailure = error => {
  return {
    type: GET_CURRENT_USER_FAILURE,
    error
  }
}

export const updateUserLocationTemporarily = locationId => {
  return {
    type: UPDATE_USER_LOCATION_TEMPORARILY,
    payload: { locationId }
  }
}

export const updateAndSaveUserLocationStart = locationId => {
  return {
    type: UPDATE_AND_SAVE_USER_LOCATION,
    payload: { locationId }
  }
}

export const updateAndSaveUserLocationSuccess = currentUser => {
  return {
    type: UPDATE_AND_SAVE_USER_LOCATION_SUCCESS,
    payload: { currentUser }
  }
}

export const updateAndSaveUserLocationFailure = error => {
  return {
    type: UPDATE_AND_SAVE_USER_LOCATION_FAILURE,
    error
  }
}

export const revertTemporaryLocation = originalLocationId => {
  return {
    type: REVERT_TEMPORARY_LOCATION,
    payload: { originalLocationId }
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------

const ACTION_HANDLERS = {
  [LOGIN]: (state, action) => {
    const { accessToken } = action.payload
    return {
      ...state,
      submitting: true,
      accessToken,
      currentUser: undefined,
      isAuthenticated: false
    }
  },
  [LOGIN_SUCCESS]: (state, action) => {
    try {
      const response = action.payload
      removeTokens()
      storeTokens(response)

      /* eslint-disable camelcase */
      const { user_id, location_id, user_name } = jwt_decode(
        response.access_token
      )
      /* eslint-enable camelcase */

      let currentUser = {
        userId: user_id,
        userName: user_name,
        email: user_name,
        deliveryLocationId: location_id
      }

      return {
        ...state,
        submitting: undefined,
        error: undefined,
        isAuthenticated: true,
        confirmCredentials: undefined,
        isCurrentUserLoading: false,
        currentUser,
        ref: undefined
      }
    } catch (ex) {
      logException(ex)
      return {
        ...state,
        submitting: undefined,
        error: ex
      }
    }
  },
  [LOGIN_FAILURE]: (state, action) => {
    const { error } = action
    return {
      ...state,
      error,
      submitting: undefined,
      isAuthenticated: false,
      currentUser: undefined
    }
  },
  [LOGOUT]: (state, action) => {
    return {
      ...state,
      ref: action.payload.ref,
      isAuthenticated: false,
      currentUser: undefined
    }
  },
  [LOGOUT_SUCCESS]: state => {
    return {
      ...state,
      isAuthenticated: false,
      currentUser: undefined
    }
  },
  [LOGOUT_FAILURE]: state => {
    return {
      ...state,
      isAuthenticated: false,
      currentUser: undefined
    }
  },
  [GET_CURRENT_USER]: (state = { currentUser: undefined }) => {
    return {
      ...state,
      isCurrentUserLoading: true
    }
  },
  [GET_CURRENT_USER_SUCCESS]: (state, action) => {
    return {
      ...state,
      error: undefined,
      currentUser: action.payload.currentUser,
      isCurrentUserLoading: false,
      isAuthenticated: true
    }
  },
  [IS_ADMIN_SUCCESS]: (state, action) => {
    return {
      ...state,
      currentUser: { ...state.currentUser, isAdmin: action.payload.isAdmin }
    }
  },
  [GET_CURRENT_USER_FAILURE]: (state, action) => {
    const { error } = action
    return {
      ...state,
      isCurrentUserLoading: false,
      error,
      currentUser: undefined,
      isAuthenticated: false
    }
  },
  [CONFIRM_USER]: (state, action) => {
    const confirmCredentials = action.payload
    return {
      ...state,
      confirmCredentials,
      submitting: true
    }
  },
  [CONFIRM_USER_SUCCESS]: (state, action) => {
    return {
      ...state,
      submitting: undefined,
      confirmUserError: undefined
    }
  },
  [CONFIRM_USER_FAILURE]: (state, action) => {
    const error = normalizeUserCreationError(action.error.response)
    return {
      ...state,
      confirmUserError: error,
      submitting: undefined,
      currentUser: undefined
    }
  },
  [CONFIRM_PASSWORD]: state => {
    return {
      ...state,
      submitting: true
    }
  },
  [CONFIRM_PASSWORD_SUCCESS]: (state, action) => {
    return {
      ...state,
      submitting: undefined,
      confirmPasswordError: undefined
    }
  },
  [CONFIRM_PASSWORD_FAILURE]: (state, action) => {
    const error = normalizeUserCreationError(action.error.response)
    return {
      ...state,
      confirmPasswordError: error,
      submitting: undefined,
      currentUser: undefined
    }
  },
  [REGISTER]: state => {
    return {
      ...state,
      submitting: true,
      registerError: undefined
    }
  },
  // Assuming the user is returned when registration finishes
  // Assuming user can register full or partial (w/password or w/o-password both are same call.
  [REGISTER_SUCCESS]: (state, action) => {
    return {
      ...state,
      currentUser: action.payload.currentUser,
      isAuthenticated: true,
      submitting: undefined
    }
  },
  [REGISTER_FAILURE]: (state, action) => {
    return {
      ...state,
      submitting: undefined,
      currentUser: undefined,
      isAuthenticated: false,
      registerError: action.error
    }
  },
  [REGISTER_LIGHT]: state => {
    return {
      ...state,
      submitting: true,
      registerLightError: undefined
    }
  },

  [REGISTER_LIGHT_SUCCESS]: (state, action) => {
    return {
      ...state
    }
  },
  [REGISTER_LIGHT_FAILURE]: (state, action) => {
    const error = normalizeUserCreationError(action.error.response)
    return {
      ...state,
      submitting: undefined,
      currentUser: undefined,
      isAuthenticated: false,
      registerLightError: error
    }
  },
  [JOIN_USER_SUCCESS]: (state, action) => {
    return {
      ...state,
      currentUser: action.payload.currentUser,
      isAuthenticated: true
    }
  },
  [UPDATE_USER_LOCATION_TEMPORARILY]: (state, action) => {
    return {
      ...state,
      currentUser: {
        ...state.currentUser,
        originalLocationId:
          state.currentUser.originalLocationId ||
          state.currentUser.deliveryLocationId,
        deliveryLocationId: action.payload.locationId
      },
      hasChangedLocation: true
    }
  },
  [REVERT_TEMPORARY_LOCATION]: (state, action) => {
    return {
      ...state,
      currentUser: {
        ...state.currentUser,
        deliveryLocationId: action.payload.originalLocationId,
        originalLocationId: undefined
      },
      hasChangedLocation: false
    }
  },
  [UPDATE_AND_SAVE_USER_LOCATION]: (state, action) => {
    return {
      ...state,
      currentUser: {
        ...state.currentUser,
        deliveryLocationId: action.payload.locationId,
        originalLocationId: state.currentUser.deliveryLocationId
      },
      hasChangedLocation: true,
      updateUserLocationError: undefined
    }
  },
  [UPDATE_AND_SAVE_USER_LOCATION_SUCCESS]: (state, action) => {
    return {
      ...state,
      currentUser: {
        ...action.payload.currentUser,
        originalLocationId: undefined
      },
      hasChangedLocation: true
    }
  },
  [UPDATE_AND_SAVE_USER_LOCATION_FAILURE]: (state, action) => {
    return {
      ...state,
      updateUserLocationError: action.error,
      currentUser: {
        ...state.currentUser,
        deliveryLocationId: state.currentUser.originalLocationId,
        originalLocationId: undefined
      }
    }
  },
  [SUCCESS]: state => {
    return {
      ...state,
      hasChangedLocation: false
    }
  }
}

// ------------------------------------
// Reducer
// ------------------------------------

const initialState = {
  submitting: undefined,
  isCurrentUserLoading: true,
  isAuthenticated: false
}

export default function user(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

// ------------------------------------
// Sagas
// ------------------------------------

export function* watchLogin() {
  while (true) {
    const {
      payload: { credentials, ref }
    } = yield take(LOGIN)
    try {
      const selector = yield select(selectRef)
      const connectionId = credentials.connectionId
      yield call(
        formSaga,
        'login',
        loginUser,
        [credentials],
        loginSuccess,
        loginFailure
      )
      const currentUser = yield select(selectCurrentUser)
      if (currentUser) {
        try {
          // Persist any local cart that has been created
          const { dropoffId } = yield select(selectCurrentDeliveryDropoff)
          if (dropoffId) {
            const { orderItems } = getLocalCart(dropoffId)
            if (orderItems.length) {
              // save them to the DB (synchronously)
              yield addItemsToCart(dropoffId, orderItems)

              //  Delete the old cart from local storage
              deleteLocalCart()
            }
          }
        } catch (ex) {
          logException(ex)
          // IGNORE
        } finally {
          let url
          // Do any redirects
          if (ref) {
            url = ref
          } else if (selector && selector.ref) {
            url = selector.ref
          } else if (connectionId) {
            url = friendsRoute.path + `?connectionId=${connectionId}`
          } else {
            const locationId = yield select(selectUserLocationId)
            if (locationId) {
              url = formatUrl(locationRoute.path, { locationId: locationId })
            } else {
              url = locationRoute.path
            }
          }

          yield put(push(url))
        }
      } else {
        // this is handled by LOGIN_FAILURE
      }
    } catch (ex) {
      logException(ex)
      yield put(loginFailure(ex))
    }
  }
}

export function* watchLogout() {
  while (true) {
    yield take(LOGOUT)
    try {
      const id = yield select(getLocationIdParam)
      const route = id ? null : loginRoute.path

      try {
        yield call(removeTokens)
        try {
          yield call(logoutAdmin)
        } catch (ex) {
          logException(ex)
        }

        yield put(logoutSuccess())
        yield put(push(route))
      } catch (ex) {
        yield put(logoutFailure())
      }
    } catch (ex) {
      yield put(logoutFailure(ex))
      yield put(push(loginRoute.path))
    }
  }
}

export function* watchLoadCurrentUser() {
  while (true) {
    yield take(GET_CURRENT_USER)
    try {
      const token = yield call(getDecodedAccessToken)
      if (token) {
        yield put(
          currentUserSuccess({
            userId: token.user_id,
            userName: token.user_name,
            email: token.user_name,
            deliveryLocationId: token.location_id
          })
        )
      } else {
        yield put(currentUserFailure())
      }
    } catch (ex) {
      yield put(currentUserFailure(ex))
    }
  }
}

export function* watchAdmin() {
  while (true) {
    const {
      payload: { currentUser }
    } = yield take(GET_CURRENT_USER_SUCCESS)

    if (process.env.REACT_APP_LOGROCKET_ID) {
      LogRocket.identify(currentUser.userId, {
        name: currentUser.name,
        email: currentUser.email
      })
    }
    yield call(apiSaga, isAdmin, [], isAdminSuccess, isAdminFailure)
  }
}

export function* watchConfirmUser() {
  while (true) {
    const {
      payload: { formData }
    } = yield take(CONFIRM_USER)
    formData.locationId = Number(formData.locationId)

    yield call(
      formSaga,
      'confirmregister',
      oneShotRegister,
      [formData],
      confirmRegisterSuccess,
      confirmRegisterFailure
    )
  }
}

export function* watchConfirmPassword() {
  while (true) {
    const {
      payload: { formData }
    } = yield take(CONFIRM_PASSWORD)
    const user = yield select(selectCurrentUser)
    yield call(
      formSaga,
      'confirmpassword',
      confirmUser,
      [user.userId, formData],
      confirmPasswordSuccess,
      confirmPasswordFailure
    )
  }
}

export function* watchSubmitRegister() {
  while (true) {
    const { payload: email } = yield take(REGISTER)
    yield call(
      formSaga,
      'registration',
      joinUser,
      [email],
      registerSuccess,
      registerFailure,
      loginRoute.path
    )
  }
}

export function* watchSubmitRegisterLight() {
  while (true) {
    const {
      payload: {
        userData: { username, locationId }
      }
    } = yield take(REGISTER_LIGHT)
    try {
      yield call(
        formSaga,
        'registrationLight',
        joinUser,
        [username, locationId],
        registerLightSuccess,
        registerLightFailure
      )
    } catch (ex) {
      yield put(registerLightFailure(ex))
    }
  }
}

export function* watchRegisterLightSuccess() {
  while (true) {
    const {
      payload: { username }
    } = yield take(REGISTER_LIGHT_SUCCESS)
    yield put(loginStart({ userName: username, password: '' }))
  }
}

export function* watchConfirmUserSuccess() {
  while (true) {
    const {
      payload: { path }
    } = yield take(CONFIRM_USER)
    yield take(CONFIRM_USER_SUCCESS)
    const credentials = yield select(selectConfirmUserCredentials)
    yield put(
      loginStart(
        {
          userName: credentials.email,
          password: credentials.password
        },
        path
      )
    )
  }
}

export function* watchConfirmPasswordSuccess() {
  while (true) {
    yield take(CONFIRM_PASSWORD_SUCCESS)
    const id = yield select(selectProfile)
    yield call(
      apiSaga,
      loadProfileByUserId,
      [id.userId],
      loadProfileSuccess,
      loadProfileFailure
    )
  }
}

export function* watchUpdateAndSaveUserLocation() {
  while (true) {
    const {
      payload: { locationId }
    } = yield take(UPDATE_AND_SAVE_USER_LOCATION)
    const user = yield select(selectCurrentUser)

    try {
      // update the user's location via API
      yield call(apiSaga, updateUserLocation, [user.userId, locationId])

      user.deliveryLocationId = locationId
      // update user token
      yield call(refreshTokenSaga)
      yield put(updateAndSaveUserLocationSuccess(user))
    } catch (ex) {
      yield put(updateAndSaveUserLocationFailure(ex))
    }
  }
}

export function* watchRevertTemporaryLocation() {
  while (true) {
    const {
      payload: { originalLocationId }
    } = yield take(REVERT_TEMPORARY_LOCATION)
    yield put(
      push(formatUrl(locationRoute.path, { locationId: originalLocationId }))
    )
  }
}

// ------------------------------------
// APIs
// ------------------------------------

export const confirmUser = (id, formData) =>
  callApi.put(`/api/v1/uaa/users/${id}/confirm`, formData)

export const oneShotRegister = formData =>
  callApi.post(`/api/v1/uaa/users/createUser`, formData)

export const loginUser = ({ userName, password }) => {
  let formData = new FormData()

  formData.append('grant_type', 'password')
  formData.append('scope', 'consumer')
  formData.append('username', userName)
  formData.append('password', password)

  return callApi.post(`/api/v1/uaa/oauth/token`, formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    auth: {
      username: CLIENT_ID,
      password: CLIENT_SECRET
    }
  })
}

export const isAdmin = () => callApi.post(`/api-monolith/user/admin-check`)

export const logoutAdmin = () => callApi.get(`/admin/logout`)

export const updateUserLocation = (id, locationId) =>
  callApi.put(`/api/v1/uaa/users/${id}/profiles`, { locationId })
